// @tennis-matches/services/winterdoppel/sessionCostShares
// Flexible Abend-Anteile (Freies Spiel): gleichmäßig, 2 Einzel, Gast bar an X.

import { playingStarters, splitCostShares } from "./planning.js";

export const WINTERDOPPEL_SESSION_COST_PRESET = {
  EQUAL: "equal",
  SINGLES2: "singles2",
  GUEST_CASH: "guestCash",
};

function __asStr(v) {
  return String(v ?? "").trim();
}

function __roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * @returns {Map<string, number>} playerId → EUR
 */
export function buildWinterDoppelSessionCostPresetShares({
  preset,
  payerIds = [],
  collectorPlayerId = null,
  eveningTotal = 0,
} = {}) {
  const total = __roundMoney(eveningTotal);
  const payers = [...new Set((payerIds || []).map(__asStr).filter(Boolean))];
  /** @type {Map<string, number>} */
  const shares = new Map();
  if (total <= 0 || !payers.length) return shares;

  const kind = __asStr(preset) || WINTERDOPPEL_SESSION_COST_PRESET.EQUAL;

  if (kind === WINTERDOPPEL_SESSION_COST_PRESET.SINGLES2) {
    if (payers.length !== 2) {
      throw new Error("winterdoppel.sessionCosts.needTwoPayers");
    }
  }

  if (
    kind === WINTERDOPPEL_SESSION_COST_PRESET.EQUAL ||
    kind === WINTERDOPPEL_SESSION_COST_PRESET.SINGLES2
  ) {
    const parts = splitCostShares(total, payers.length);
    payers.forEach((id, i) => shares.set(id, parts[i]));
    return shares;
  }

  if (kind === WINTERDOPPEL_SESSION_COST_PRESET.GUEST_CASH) {
    const collector = __asStr(collectorPlayerId);
    if (!collector || !payers.includes(collector)) {
      throw new Error("winterdoppel.sessionCosts.needCollector");
    }
    if (payers.length === 1) {
      shares.set(collector, total);
      return shares;
    }
    const cents = Math.round(total * 100);
    const collectorCents = Math.round(cents / 2);
    const restCents = cents - collectorCents;
    shares.set(collector, collectorCents / 100);
    const others = payers.filter((id) => id !== collector);
    const otherParts = splitCostShares(restCents / 100, others.length);
    others.forEach((id, i) => shares.set(id, otherParts[i]));
    return shares;
  }

  throw new Error("winterdoppel.sessionCosts.invalidPreset");
}

/**
 * Setzt cost_share der aktiven Starter laut shareMap; andere aktive Starter → 0.
 * Absagen / Kosten-only-Zeilen (rsvp=no) bleiben unberührt.
 */
export function applyWinterDoppelSessionCostShareMap(
  sessionPlayers = [],
  shareMap = new Map(),
) {
  const next = (sessionPlayers || []).map((sp) => ({ ...sp }));
  const map =
    shareMap instanceof Map
      ? shareMap
      : new Map(
          Object.entries(shareMap || {}).map(([k, v]) => [
            __asStr(k),
            __roundMoney(v),
          ]),
        );

  for (const sp of next) {
    if (!sp || sp.role !== "starter" || sp.rsvp === "no") continue;
    const pid = __asStr(sp.player_id);
    sp.cost_share = map.has(pid) ? __roundMoney(map.get(pid)) : 0;
  }
  return next;
}

export function applyWinterDoppelSessionCostPreset(
  sessionPlayers = [],
  {
    preset = WINTERDOPPEL_SESSION_COST_PRESET.EQUAL,
    eveningTotal = 0,
    collectorPlayerId = null,
    payerPlayerIds = null,
  } = {},
) {
  const playing = playingStarters(sessionPlayers);
  const payers = (
    Array.isArray(payerPlayerIds) && payerPlayerIds.length
      ? payerPlayerIds
      : playing.map((sp) => sp.player_id)
  )
    .map(__asStr)
    .filter(Boolean);

  const poolFromPlaying = __roundMoney(
    playing.reduce((s, sp) => s + (Number(sp.cost_share) || 0), 0),
  );
  // Bestehende Anteile erhalten (Saison-Abo), sonst Abendpreis als Fallback
  const pool =
    poolFromPlaying > 0 ? poolFromPlaying : __roundMoney(eveningTotal);

  const shareMap = buildWinterDoppelSessionCostPresetShares({
    preset,
    payerIds: payers,
    collectorPlayerId,
    eveningTotal: pool,
  });
  return applyWinterDoppelSessionCostShareMap(sessionPlayers, shareMap);
}
